import { useFormContext, useWatch } from "react-hook-form";

import {
  CommonLabel,
  ContentWrapper,
  InputAndLabelWrapper,
  InputRowWrapper,
  LabelAndInfoWrapper,
  SingleColumnWrapper,
} from "../styles";
import CustomSelect from "../inputs/CustomSelect";
import CustomSelectWithCheckbox from "../inputs/CustomSelectWithCheckbox";
import { useTranslation } from "react-i18next";
import { KYC_ASSET_VALUES, KYC_OTHER, PROPERTY_ASSET_VALUES } from "./utils";
import { useReducer, useState } from "react";
import { InfoModal } from "../../../../shared/modals/InfoModal";
import InfoButton from "../../../../shared/button/InfoButton";
import TextInputWithLanguageControl from "../inputs/TextInputWithLanguageControl";

function Kyc(props) {
  const {
    moneySourcesOptions,
    monthlyTurnovers,
    assets,
    propertySources,
    showCounterPartiesQuestion,
  } = props;

  const { t } = useTranslation();

  const { setValue } = useFormContext();

  const { assetVal, moneySourceVal, propSourceVal } = KYC_OTHER;

  const [modalIsOpen, setModalIsOpen] = useState(false);

  const [infoModal, setInfoModal] = useReducer(
    (info, newInfo) => ({ ...info, ...newInfo }),
    { heading: "", body: "" },
  );

  const assetsValue = useWatch({ name: "kyc.assets" });
  const showOtherAsset = assetsValue?.some((item) => item?.value === assetVal);
  const hasPropertyAsset = assetsValue?.some((item) =>
    PROPERTY_ASSET_VALUES.includes(item?.value),
  );
  const hasNoAssets = assetsValue?.some(
    (item) => item?.value === KYC_ASSET_VALUES.noAssets,
  );

  const moneySourcesValue = useWatch({ name: "kyc.moneySourcesOptions" });

  const showOtherMoneySource = moneySourcesValue?.some(
    (item) => item?.value === moneySourceVal,
  );

  const propertySourcesValue = useWatch({ name: "kyc.propertySources" });

  const showOtherPropertySource = propertySourcesValue?.some(
    (item) => item?.value === propSourceVal,
  );

  function handleMoneySourceChange(options) {
    const hasOther = options?.some((item) => item?.value === moneySourceVal);

    if (!hasOther) {
      setValue("kyc.otherMoneySources", null);
    }
  }

  function handlePropertySourceSourceChange(options) {
    const hasOther = options?.some((item) => item?.value === propSourceVal);

    if (!hasOther) {
      setValue("kyc.otherPropertySources", null);
    }
  }

  function handleAssetsChange(options) {
    let selectedAssets = options || [];

    const hasPropertyAsset = selectedAssets?.some((item) =>
      PROPERTY_ASSET_VALUES.includes(item?.value),
    );
    const hasNoAssets = selectedAssets?.some(
      (item) => item?.value === KYC_ASSET_VALUES.noAssets,
    );

    if (hasPropertyAsset && hasNoAssets) {
      const hadNoAssets = assetsValue?.some(
        (item) => item?.value === KYC_ASSET_VALUES.noAssets,
      );

      selectedAssets = selectedAssets.filter((item) => {
        if (hadNoAssets) {
          return item?.value !== KYC_ASSET_VALUES.noAssets;
        }

        return item?.value === KYC_ASSET_VALUES.noAssets;
      });

      setValue("kyc.assets", selectedAssets, { shouldValidate: true });
    }

    const hasOther = selectedAssets?.some((item) => item?.value === assetVal);
    const hasSelectedPropertyAsset = selectedAssets?.some((item) =>
      PROPERTY_ASSET_VALUES.includes(item?.value),
    );

    if (!hasOther) {
      setValue("kyc.otherAssets", null);
    }

    if (!hasSelectedPropertyAsset) {
      setValue("kyc.propertySources", null);
      setValue("kyc.otherPropertySources", null);
    }
  }

  function handleShowInfoAboutCounterParties() {
    openModal();
    setInfoModal({
      heading: t("InformationOnCounterpartiesPlaceholder"),
      body: t("InformationOnCounterparties"),
    });
  }

  function openModal() {
    setModalIsOpen(true);
  }

  return (
    <ContentWrapper>
      <SingleColumnWrapper>
        <InputAndLabelWrapper>
          <CommonLabel>{t("pep_estimated_cash_turnover")}</CommonLabel>
          <CustomSelectWithCheckbox
            name="kyc.moneySourcesOptions"
            options={moneySourcesOptions}
            rules={{ required: t("required") }}
            handleChange={handleMoneySourceChange}
            maxWidth={showOtherMoneySource && 400}
          />
        </InputAndLabelWrapper>
        {showOtherMoneySource ? (
          <InputAndLabelWrapper>
            <TextInputWithLanguageControl
              name="kyc.otherMoneySources"
              label={t("pep_SourceOfCashFunds")}
              rules={{ required: t("required") }}
              isLabelHidden
            />
          </InputAndLabelWrapper>
        ) : null}
      </SingleColumnWrapper>
      <InputRowWrapper>
        <InputAndLabelWrapper>
          <CommonLabel>{t("pep_SourceOfCashFunds")}</CommonLabel>
          <CustomSelect
            name="kyc.estimatedCash"
            options={monthlyTurnovers}
            rules={{
              required: t("required"),
            }}
          />
        </InputAndLabelWrapper>
      </InputRowWrapper>
      <SingleColumnWrapper>
        <InputAndLabelWrapper>
          <CommonLabel>{t("pep_informationRegardingAssets")}</CommonLabel>
          <CustomSelectWithCheckbox
            name="kyc.assets"
            options={assets}
            rules={{
              required: t("required"),
            }}
            handleChange={handleAssetsChange}
            maxWidth={showOtherAsset && 400}
          />
        </InputAndLabelWrapper>
        {showOtherAsset ? (
          <InputAndLabelWrapper>
            <TextInputWithLanguageControl
              name="kyc.otherAssets"
              label={t("pep_informationRegardingAssets")}
              rules={{ required: t("required") }}
              isLabelHidden
            />
          </InputAndLabelWrapper>
        ) : null}
      </SingleColumnWrapper>

      {hasPropertyAsset && !hasNoAssets ? (
        <SingleColumnWrapper>
          <InputAndLabelWrapper>
            <CommonLabel>{t("pep_sourceOfProperty")}</CommonLabel>
            <CustomSelectWithCheckbox
              name="kyc.propertySources"
              options={propertySources}
              rules={{
                required: t("required"),
              }}
              handleChange={handlePropertySourceSourceChange}
              maxWidth={showOtherPropertySource && 400}
            />
          </InputAndLabelWrapper>
          {showOtherPropertySource ? (
            <InputAndLabelWrapper>
              <TextInputWithLanguageControl
                name="kyc.otherPropertySources"
                label={t("pep_sourceOfProperty")}
                rules={{ required: t("required") }}
                isLabelHidden
              />
            </InputAndLabelWrapper>
          ) : null}
        </SingleColumnWrapper>
      ) : null}

      {showCounterPartiesQuestion ? (
        <InputRowWrapper>
          <InputRowWrapper>
            <InputAndLabelWrapper>
              <TextInputWithLanguageControl
                name="kyc.infoCounterparties"
                label={t("kyc_infoCounterparties")}
                rules={{ required: t("required") }}
                customLabel={
                  <LabelAndInfoWrapper>
                    <CommonLabel htmlFor="kyc_infoCounterparties">
                      {t("kyc_infoCounterparties")}
                    </CommonLabel>
                    <InfoButton
                      className="ml-2"
                      onClick={handleShowInfoAboutCounterParties}
                    />
                  </LabelAndInfoWrapper>
                }
              />
            </InputAndLabelWrapper>
          </InputRowWrapper>
        </InputRowWrapper>
      ) : null}

      {modalIsOpen ? (
        <InfoModal
          isOpen={modalIsOpen}
          onToggle={() => setModalIsOpen(false)}
          heading={infoModal.heading}
          body={infoModal.body}
        />
      ) : (
        false
      )}
    </ContentWrapper>
  );
}
export default Kyc;
